import { HowToVote } from "@mui/icons-material";
import { Alert, Button, Checkbox, Grid, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Snackbar, Stack, Typography } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Api from "../api/Api";
import ConfirmDialog from "../components/ConfirmDialog";
import ViewPollCard from "../components/ViewPollCard";
import { AppBarContext } from "../context/AppBarContextProvider";
import { TangledContext } from "../context/TangledContextProvider";
import { UserContext } from "../context/UserContextProvider";
import PollTimer from "../components/PollTimer";

const ViewPollPage = () => {
    const tangled = useContext(TangledContext);
    const { id } = useParams();
    const { user, setUser } = useContext(UserContext);
    const { setTitle } = useContext(AppBarContext);
    const [poll, setPoll] = useState(undefined);
    const [selectedOption, setSelectionOption] = useState(undefined);
    const [hasVoted, setHasVoted] = useState(false);
    const [votes, setVotes] = useState({ total: 0, counts: {} });
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [alertState, setAlertState] = useState({ show: false, success: true, message: '' })

    useEffect(() => {
        if (!id || !user?.idem_guid) {
            return;
        }

        const fetchPoll = async () => {
            const isGuest = user.idem_guid === 'guest';
            let data = await Api.getPoll(id, user.idem_guid);
            if (data?._id) {
                const canVote = data.blockTangledGroups && user.user_name_formatted?.startsWith('g/') ? false : true;
                let mVotes;
                if (data.hasVoted || data.status === 2 || isGuest || !canVote) {
                    mVotes = await Api.getVotes(id);
                }
                setPoll(data);
                setTitle(data.name);
                setHasVoted(data.hasVoted || isGuest || !canVote);
                setVotes(mVotes)
            }
        }
        fetchPoll();
        return () => setTitle(undefined)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, setTitle, user])

    useEffect(() => {
        const fetchUser = async () => {
            const timeoutHandler = setTimeout(() => setUser({ idem_guid: 'guest' }), 1200)
            let data = await tangled.current.getUserData();
            clearTimeout(timeoutHandler);
            if (!data?.idem_guid) {
                data = { idem_guid: 'guest' };
            } else {
                await Api.upsertUser({tangledUserName: data.user_name_formatted, tangledIdemGUID: data.idem_guid});
            }
            setUser(data)
        };
        fetchUser();
    }, [setUser, tangled])

    if (!poll) {
        return null;
    }

    const handleCloseFormUpdateAlert = (/** @type {any} */ _event, /** @type {string} */ reason) => {
        if (reason === 'clickaway') {
            return
        }

        setAlertState({ show: false, success: true, message: '' })
    }

    const castVote = async () => {
        setShowConfirmDialog(false)
        try {
            if (user.idem_guid === 'guest') {
                throw Error('guest_vote_unauthorized');
            }

            let ledgerGUID;
            if (poll.voteFee > 0) {
                const amount = poll.voteFee.toLocaleString('en-US');
                const response = await tangled.current.requestPayment(
                    amount,
                    0,
                    `Pay ${amount} mlx to vote in the poll "${poll.question}".`,
                );

                if (response.status === 'fail') {
                    setAlertState({ show: true, success: false, message: `Could not process the payment.` })
                    return;
                }

                ledgerGUID = response.ledger_guid_application_deposit;
            }
            setVotes(await Api.castVote(poll._id, selectedOption, user.idem_guid, user.user_name_formatted, ledgerGUID));
            setHasVoted(true)
            setAlertState({ show: true, success: true, message: `Your vote was added to the poll.` });
        } catch (e) {
            setAlertState({ show: true, success: false, message: `Could not add your vote to the poll.` })
        }
    }

    const viewVotes = async () => {
        setVotes(await Api.getVotes(id))
        setHasVoted(true);
    }
    return <Grid container spacing={2} p={2}>
        <Grid item xs={12}>
            <Typography variant={'h6'} sx={{ color: 'white' }}>
                {poll.question}
            </Typography>
        </Grid>
        {(hasVoted || poll.status === 2) && <ViewPollCard poll={poll} initialVotes={votes} />}
        {!hasVoted && poll.status === 1 && <Grid item xs={12}>
            <List sx={{ borderColor: '#90caf996', borderWidth: '1px', borderStyle: 'solid', borderRadius: '4px', p: 0 }}>
                {poll.options.map((/** @type {{ option: string; _id:String; }} */ item) => {
                    const labelId = `checkbox-list-label-${item._id}`;
                    return (
                        <ListItem
                            key={item.option}
                            disablePadding
                        >
                            <ListItemButton onClick={() => setSelectionOption(item._id)}>
                                <ListItemIcon>
                                    <Checkbox
                                        edge="start"
                                        checked={selectedOption === item._id}
                                        disableRipple
                                        inputProps={{ 'aria-labelledby': labelId }}
                                    />
                                </ListItemIcon>
                                <ListItemText id={labelId} primary={item.option} sx={{ color: 'white' }} />
                            </ListItemButton>
                        </ListItem>
                    );
                })}
            </List>
        </Grid>}
        <PollTimer poll={poll} onClosed={() => setPoll({ ...poll, status: 2 })} />
        <Grid item xs={12} justifyContent={'center'} display={'flex'}>
            {!hasVoted && poll.status === 1 && <Stack direction={'row'} spacing={2}>
                <Button startIcon={<HowToVote />} variant={'contained'} onClick={castVote} disabled={!selectedOption}>
                    Vote
                </Button>
                <Button variant={'text'} onClick={viewVotes}>
                    View votes
                </Button>
            </Stack>}
            {poll.status === 2 && <Alert variant='outlined' severity={'info'}>
                This poll is closed.
            </Alert>}
        </Grid>
        {alertState.show && (
            <Snackbar
                open={alertState.show}
                autoHideDuration={4000}
                onClose={handleCloseFormUpdateAlert}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseFormUpdateAlert}
                    severity={alertState.success ? 'success' : 'error'}
                    variant={'filled'}
                    elevation={6}
                    sx={{ width: '100%' }}
                >
                    {alertState.message}
                </Alert>
            </Snackbar>
        )}
        {showConfirmDialog && (
            <ConfirmDialog
                open={true}
                dialogTitle={'Cast Your Vote'}
                contentText={`Do you want to vote on this poll?`}
                confirmText={'Vote'}
                cancelText={'Cancel'}
                onCancel={() => setShowConfirmDialog(false)}
                confirmStartIcon={<HowToVote />}
                onConfirm={castVote}
            ></ConfirmDialog>
        )}
    </Grid>
}

export default ViewPollPage;